import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsIn,
  IsInt,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { FIELD_VALUE_SOURCES, type FieldValueSource } from '../../label/field-value-source';
import type { IngredientVerticalOriginEntrySchema } from '../../label/prepackaged-ingredient-vertical-origin';
import { ADDITIVE_FUNCTIONS, type AdditiveFunction } from './additive-function.enum';
import { BREAKDOWN_EXEMPTIONS, type BreakdownExemption } from './breakdown-exemption.enum';
import { IngredientAllergenHistoryResponseDto } from './ingredient-allergen-history-response.dto';
import { IngredientAllergenResponseDto } from './ingredient-allergen-response.dto';
import { IngredientComponentResponseDto } from './ingredient-component-response.dto';
import {
  INGREDIENT_PHYSICAL_KINDS,
  type IngredientPhysicalKind,
} from './ingredient-physical-kind.enum';
import { INGREDIENT_PROVENANCES, type IngredientProvenance } from './ingredient-provenance.enum';

/**
 * Stato allergeni dell'anagrafica (DEV-375, D5): tre valori, mai un "nessuno"
 * dedotto da un'assenza.
 *  - `DECLARED_SOME`: almeno un allergene dichiarato e tutto il sottoalbero di
 *    composizione dichiarato e visibile.
 *  - `DECLARED_NONE`: dichiarato "nessun allergene" (es. sale), nessun nodo ignoto.
 *  - `UNKNOWN`: un nodo del sottoalbero mai dichiarato (`isCompound` null senza
 *    allergeni) oppure un ciclo/limite di composizione raggiunto, ANCHE se altri
 *    allergeni sono noti (fail-closed: un latte noto non nasconde un ramo non
 *    dichiarato; la UI mostra i chip noti piu' "Allergeni da completare").
 * Calcolato dal SQL sul sottoalbero, che non ha il tetto di 200 percorsi
 * (TOO_LARGE) del caricatore del grafo: e' un superset, mai un allergene perso.
 */
export const INGREDIENT_ALLERGEN_STATES = ['DECLARED_SOME', 'DECLARED_NONE', 'UNKNOWN'] as const;
export type IngredientAllergenState = (typeof INGREDIENT_ALLERGEN_STATES)[number];

/**
 * `tenantId` is null exactly on the global platform catalog.
 * `isGlobalCatalogEntry` mirrors that as an explicit boolean so the frontend
 * never re-derives visibility by checking `tenantId === null` in a dozen
 * places (clean-code "no mental mapping").
 */
export class IngredientResponseDto {
  @IsUUID()
  id!: string;

  @IsOptional()
  @IsUUID()
  tenantId?: string | null;

  @IsBoolean()
  isGlobalCatalogEntry!: boolean;

  @IsString()
  name!: string;

  /** Denominazione legale in etichetta; `null` = si usa il nome di anagrafica. */
  @IsOptional()
  @IsString()
  labelName?: string | null;

  @IsString()
  normalizedName!: string;

  @IsIn(FIELD_VALUE_SOURCES)
  nameSource!: FieldValueSource;

  @IsArray()
  @IsString({ each: true })
  synonyms!: string[];

  @IsIn(INGREDIENT_PHYSICAL_KINDS)
  physicalKind!: IngredientPhysicalKind;

  @IsOptional()
  @IsIn(ADDITIVE_FUNCTIONS)
  additiveFunction?: AdditiveFunction | null;

  @IsOptional()
  @IsString()
  additiveENumber?: string | null;

  @IsOptional()
  @IsIn(BREAKDOWN_EXEMPTIONS)
  breakdownExemption?: BreakdownExemption | null;

  /** Deliberate tri-state (see the `Ingredient.isCompound` schema comment):
   * `null` = nobody has declared yet, `false` = declared simple, `true` =
   * compound. */
  @IsOptional()
  @IsBoolean()
  isCompound?: boolean | null;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  densityGPerMl?: number | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  densitySource?: FieldValueSource | null;

  /// Peso medio di un pezzo in grammi (NUMERO -> peso). NULL se non si vende/usa a pezzo.
  @IsOptional()
  @IsNumber()
  @IsPositive()
  pieceWeightG?: number | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  pieceWeightSource?: FieldValueSource | null;

  /// Frazione di parte edibile (0,1]. Tri-state NULL = incompleto, MAI default 1.
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1)
  ediblePortionFactor?: number | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  ediblePortionSource?: FieldValueSource | null;

  /// Marca commerciale dedicata (es. "Barilla"). NULL per ingredienti generici.
  @IsOptional()
  @IsString()
  brand?: string | null;

  /// Brand normalizzato per lookup/unicita. NULL quando `brand` e' NULL.
  @IsOptional()
  @IsString()
  normalizedBrand?: string | null;

  /// Catalogo/sorgente esterno a cui `externalCode` appartiene (es. codice fornitore).
  @IsOptional()
  @IsString()
  catalogSource?: string | null;

  /// Codice dentro il catalogo `catalogSource`.
  @IsOptional()
  @IsString()
  externalCode?: string | null;

  /// Stato di provenienza del dato catalogo.
  @IsOptional()
  @IsIn(INGREDIENT_PROVENANCES)
  provenance?: IngredientProvenance;

  @IsInt()
  @Min(1)
  labelDataVersion!: number;

  /**
   * Versione della riga per il blocco ottimistico ("Sposta in archivio" manda
   * `expectedUpdatedAt`): va presa dalla risposta dell'ultimo salvataggio o
   * della lettura, mai dallo stato del form.
   */
  @IsDate()
  updatedAt!: Date;

  @IsOptional()
  @IsUUID()
  curatedById?: string | null;

  @IsOptional()
  @IsDate()
  curatedAt?: Date | null;

  @IsOptional()
  @IsString()
  changeNote?: string | null;

  /** Presigned URL of the ingredient photo, resolved at read time (null when
   * the entry has no photo). Visual aid only — never label data. */
  @IsOptional()
  @IsString()
  photoUrl?: string | null;

  /** Stato allergeni calcolato dal backend sull'intero sottoalbero di composizione (fonte unica: il frontend non lo deriva). */
  @IsIn(INGREDIENT_ALLERGEN_STATES)
  allergenState!: IngredientAllergenState;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientAllergenResponseDto)
  allergens?: IngredientAllergenResponseDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientComponentResponseDto)
  components?: IngredientComponentResponseDto[];

  /** Quanti ingredienti lo usano come componente collegato (ogni tenant per un ingrediente globale). */
  @IsOptional()
  @IsInt()
  @Min(0)
  linkedFromCount?: number;

  /**
   * Usato per l'etichettatura (DEV-375): presente in almeno una riga di ricetta
   * OPPURE come voce diretta di un menu OPPURE collegato come componente di un
   * altro ingrediente. Per un ingrediente globale conta solo l'uso del tenant
   * che legge. Decide se "allergeni da dichiarare" e' un avviso (usato) o una
   * semplice nota neutra (mai usato, es. creato da un DDT e mai impiegato).
   */
  @IsBoolean()
  usedForLabels!: boolean;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientAllergenHistoryResponseDto)
  history?: IngredientAllergenHistoryResponseDto[];

  @IsOptional()
  @IsArray()
  verticalOrigin?: IngredientVerticalOriginEntrySchema[] | null;
}
